import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../shared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

/**
 * 🤝 SubagentApprovalDialog — supplier picker that opens when an accountant
 * approves a ticket/trip/operation belonging to a Subagent (B2B partner)
 * agency. Per the agreed flow (option C), the choice is MANDATORY:
 *   • Pick a supplier + cost  → SALE_DUE row written to supplier_ledger
 *   • OR explicitly mark as «خدمة صنهاجة داخلية»  → no ledger entry
 */
const SubagentApprovalDialog = ({
  open,
  onClose,
  onConfirm,
  // Optional metadata to display in the header
  itemLabel,        // "تذكرة TKT-…" / "رحلة A-…" / "عملية OP-…"
  agencyName,       // subagent agency name
  sellPrice,        // for profit hint
  loading,
}) => {
  const [suppliers, setSuppliers] = useState([]);
  const [mode, setMode] = useState('supplier'); // 'supplier' | 'internal'
  const [supplierId, setSupplierId] = useState('');
  const [supplierServiceId, setSupplierServiceId] = useState('');
  const [supplierServices, setSupplierServices] = useState([]);
  const [supplierCost, setSupplierCost] = useState('');
  const [error, setError] = useState('');

  // Load active suppliers when opened
  useEffect(() => {
    if (!open) return;
    setMode('supplier');
    setSupplierId('');
    setSupplierServiceId('');
    setSupplierServices([]);
    setSupplierCost('');
    setError('');
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/suppliers?status=active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuppliers(r.data || []);
      } catch (_e) {
        setSuppliers([]);
      }
    })();
  }, [open]);

  // Load services for the picked supplier
  useEffect(() => {
    if (!supplierId) { setSupplierServices([]); setSupplierServiceId(''); return; }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/suppliers/${supplierId}/services?active_only=true`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSupplierServices(r.data || []);
      } catch (_e) {
        setSupplierServices([]);
      }
    })();
  }, [supplierId]);

  const handleSubmit = () => {
    setError('');
    if (mode === 'internal') {
      onConfirm({ service_origin: 'internal' });
      return;
    }
    if (!supplierId) {
      setError('❌ يرجى اختيار المورد أولاً، أو اختر «خدمة صنهاجة داخلية»');
      return;
    }
    if (!supplierCost || isNaN(parseFloat(supplierCost)) || parseFloat(supplierCost) < 0) {
      setError('❌ تكلفة المورد إلزامية ولا تكون سالبة');
      return;
    }
    onConfirm({
      supplier_id: supplierId,
      supplier_service_id: supplierServiceId || null,
      supplier_cost: parseFloat(supplierCost),
    });
  };

  const profit = (() => {
    const c = parseFloat(supplierCost || 0);
    const s = parseFloat(sellPrice || 0);
    if (!c && !s) return null;
    return s - c;
  })();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="subagent-approval-dialog">
        <DialogHeader>
          <DialogTitle>📦 ربط بمورد قبل الاعتماد</DialogTitle>
          <DialogDescription>
            {itemLabel ? <span className="font-mono ml-1">{itemLabel}</span> : null}
            وكيل مُناوِل: <strong>{agencyName || '—'}</strong>
            <br />
            <span className="text-amber-700 text-xs">
              🔔 إلزامي: اختر المورد المرتبط بهذه العملية أو حدّد أنها «خدمة صنهاجة داخلية».
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('supplier')}
              className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${mode === 'supplier' ? 'bg-yellow-100 border-yellow-500 text-yellow-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
              data-testid="approval-mode-supplier-btn"
            >
              📦 ربط بمورد
            </button>
            <button
              type="button"
              onClick={() => setMode('internal')}
              className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${mode === 'internal' ? 'bg-green-100 border-green-400 text-green-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
              data-testid="approval-mode-internal-btn"
            >
              🏢 خدمة صنهاجة داخلية
            </button>
          </div>

          {mode === 'supplier' && (
            <div className="space-y-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div>
                <Label className="text-sm font-medium">المورد *</Label>
                <select
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm"
                  data-testid="approval-supplier-select"
                >
                  <option value="">اختر مورداً...</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {suppliers.length === 0 && (
                  <p className="text-xs text-orange-600 mt-1">⚠️ لا يوجد موردون. أضف مورداً من «إدارة الموردين».</p>
                )}
              </div>

              {supplierId && supplierServices.length > 0 && (
                <div>
                  <Label className="text-xs">خدمة المورد (اختياري — تعبئة التكلفة تلقائياً)</Label>
                  <select
                    value={supplierServiceId}
                    onChange={(e) => {
                      const svcId = e.target.value;
                      setSupplierServiceId(svcId);
                      const svc = supplierServices.find((x) => x.id === svcId);
                      if (svc?.cost_price != null) setSupplierCost(String(svc.cost_price));
                    }}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm"
                    data-testid="approval-supplier-service-select"
                  >
                    <option value="">— اختر خدمة —</option>
                    {supplierServices.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} {s.cost_price != null ? `(تكلفة ${Number(s.cost_price).toLocaleString()})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium">تكلفة المورد (دج) *</Label>
                <Input
                  type="number"
                  value={supplierCost}
                  onChange={(e) => setSupplierCost(e.target.value)}
                  placeholder="ما تدفعه الإدارة للمورد"
                  data-testid="approval-supplier-cost-input"
                />
                {profit !== null && (
                  <p className={`text-xs mt-1 ${profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    💰 الربح: {Number(profit).toLocaleString()} دج
                    {sellPrice ? <span className="text-gray-500"> (سعر البيع: {Number(sellPrice).toLocaleString()})</span> : null}
                  </p>
                )}
              </div>
            </div>
          )}

          {mode === 'internal' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                ✅ ستُعتمد هذه العملية كـ <strong>خدمة صنهاجة داخلية</strong> — لن تُسجَّل أي مستحقات لمورد، ولن يُحسب فرق ربح/تكلفة.
              </p>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-brand-600 hover:bg-brand-700 text-white"
            data-testid="approval-confirm-btn"
          >
            {loading ? 'جاري الاعتماد…' : '✅ تأكيد الاعتماد'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubagentApprovalDialog;
